import { useEffect, useState } from 'react';
import ArrivageCard from '../../components/ArrivageCard';
import CarteCommande from '../../components/CarteCommande';
import CommandeImage from '../../components/CommandeImage';
import { Chargement, EtatVide, MessageErreur } from '../../components/EtatListe';
import { BoutonRetour, EnTeteSection } from '../../components/ui';
import { libelleCommande } from '../../utils/commandeLabel';
import useBreakpoint from '../../utils/useBreakpoint';

/**
 * Deux niveaux, communs aux volets délavage-réception et finition : COMMANDE → ARRIVAGE. Ni
 * sous-traitant ni planning ici, contrairement à la confection — ce sont des ateliers internes
 * SIP, pas un parc de sous-traitants.
 *
 * `champId` désigne le champ qui porte l'identifiant d'ouverture sur MeasurementArrivageDTO
 * (`receptionDelavageId` en délavage, `receptionId` en finition) : c'est lui qu'on passe à
 * `ouvrirSession`.
 */
export default function CommandeArrivageDrilldown({
  titreNiveau1, sousTitreNiveau1, chargerCommandes, chargerArrivages, ouvrirSession, champId,
  onSessionOuverte,
}) {
  const { large } = useBreakpoint();
  const [commandes, setCommandes] = useState([]);
  const [loadingCommandes, setLoadingCommandes] = useState(true);
  const [erreurCommandes, setErreurCommandes] = useState(null);

  const [commandeSelectionnee, setCommandeSelectionnee] = useState(null);
  const [arrivages, setArrivages] = useState([]);
  const [loadingArrivages, setLoadingArrivages] = useState(false);
  const [erreurArrivages, setErreurArrivages] = useState(null);
  const [ouvertureEnCoursId, setOuvertureEnCoursId] = useState(null);
  // Sens de la dernière navigation : on entre par la droite, on revient par la gauche.
  const [sens, setSens] = useState(null);

  useEffect(() => {
    chargerListeCommandes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function chargerListeCommandes() {
    setLoadingCommandes(true);
    setErreurCommandes(null);
    chargerCommandes()
      .then((res) => setCommandes(res || []))
      .catch((err) => setErreurCommandes(err.message || 'Impossible de charger les commandes.'))
      .finally(() => setLoadingCommandes(false));
  }

  function choisirCommande(cmd) {
    setSens('pousser');
    setCommandeSelectionnee(cmd);
    setArrivages([]);
    setLoadingArrivages(true);
    setErreurArrivages(null);
    chargerArrivages(cmd.id)
      .then((res) => setArrivages(res || []))
      .catch((err) => setErreurArrivages(err.message || 'Impossible de charger les réceptions.'))
      .finally(() => setLoadingArrivages(false));
  }

  function ouvrirArrivage(arrivage) {
    if (arrivage.sessionId) {
      onSessionOuverte(arrivage.sessionId, { commande: commandeSelectionnee, arrivage });
      return;
    }
    const id = arrivage[champId];
    setOuvertureEnCoursId(id);
    ouvrirSession(id)
      .then((session) => onSessionOuverte(session.id, { commande: commandeSelectionnee, arrivage, session }))
      .catch((err) => setErreurArrivages(err.message || "Impossible d'ouvrir cette campagne de mesure."))
      .finally(() => setOuvertureEnCoursId(null));
  }

  const classeNav = sens === 'pousser' ? 'aqs-nav-pousser' : sens === 'revenir' ? 'aqs-nav-revenir' : undefined;

  if (!commandeSelectionnee) {
    return (
      <div key="commandes" className={classeNav}>
        <EnTeteSection titre={titreNiveau1} sousTitre={sousTitreNiveau1} />

        <div style={{ marginTop: 22 }}>
          {loadingCommandes && <Chargement texte="Chargement des commandes…" />}
          {erreurCommandes && <MessageErreur texte={erreurCommandes} onReessayer={chargerListeCommandes} />}
          {!loadingCommandes && !erreurCommandes && commandes.length === 0 && (
            <EtatVide texte="Aucune commande à mesurer pour le moment." />
          )}
          {!loadingCommandes && !erreurCommandes && commandes.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${large ? 2 : 1}, minmax(0, 1fr))`, gap: 16 }}>
              {commandes.map((c) => {
                const info = libelleCommande(c);
                return (
                  <CarteCommande
                    key={c.id}
                    info={info}
                    identifiantSecours={`#${c.id}`}
                    ctaLabel="Voir les réceptions"
                    onClick={() => choisirCommande(c)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  const infoCmd = libelleCommande(commandeSelectionnee);
  return (
    <div key="arrivages" className={classeNav}>
      <EnTeteSection
        retour={<BoutonRetour libelle={titreNiveau1} onClick={() => { setSens('revenir'); setCommandeSelectionnee(null); setArrivages([]); }} />}
        visuel={<CommandeImage imageUrl={infoCmd.imageUrl} taille={64} />}
        titre="Réceptions à mesurer"
        sousTitre={`Commande ${infoCmd.numero} · ${infoCmd.modele}`}
      />

      <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loadingArrivages && <Chargement texte="Chargement des réceptions…" />}
        {erreurArrivages && <MessageErreur texte={erreurArrivages} onReessayer={() => choisirCommande(commandeSelectionnee)} />}
        {!loadingArrivages && !erreurArrivages && arrivages.length === 0 && (
          <EtatVide texte="Aucune réception enregistrée pour cette commande." />
        )}
        {!loadingArrivages && !erreurArrivages && arrivages.map((a, i) => (
          <ArrivageCard
            key={a[champId] ?? a.sessionId ?? i}
            arrivage={a}
            onOuvrir={ouvrirArrivage}
            ouvertureEnCours={ouvertureEnCoursId === a[champId]}
          />
        ))}
      </div>
    </div>
  );
}
